import { IoCartOutline, IoTrashBinOutline } from 'react-icons/io5';
import BackButton from '../../../components/backButton';
import { useEffect, useState } from 'react';
import Nav from '../../../components/Layout/user/nav';
import useGlobalContext from '../../../hooks/useStore';
import { storeSchema } from '../../../types/store';
import { ProductType } from '../../../types/product';
import type { CartItem } from '../../../types/product';
import { IonButton, IonRippleEffect } from '@ionic/react';
import CartItemCard from './inc/cartItem';
import { useHistory, useLocation } from 'react-router';
import { deleteCartItem, getAllCartItems } from '../../../context/actions/cart';
import ErrorToast from '../../../components/error_toast';
import Spinner from '../../../components/spinner';
import { authService } from '../../../utils/authService';
import { getSession } from '../../../utils/user/generateSession';
import { MdShoppingCartCheckout } from 'react-icons/md';
import { IoMdShareAlt } from 'react-icons/io';
import { share } from '../../../utils/common';
import SEOHeader from '../../../components/SEO';

const Cart = () => {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState<{
    state: boolean;
    scope: string;
  } | null>();
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [message, setMessage] = useState<{
    type?: 'error' | 'warning' | 'success';
    text: string;
  }>();
  const { cartState, cartDispatch } = useGlobalContext();
  type storeCart = Omit<storeSchema, 'productViews'> & {
    checkout: number;
    products: Array<
      ProductType & { count: number; totalPrice: number; cartItemId: number }
    >;
  };
  const [cart, setCart] = useState<storeCart[]>([] as storeCart[]);
  let cartBreakdown: JSX.Element[] = [];

  useEffect(() => {
    const _cart = new Map<number, storeCart>();
    cartState.cart?.forEach((item: CartItem) => {
      if (!_cart.has(item.store.id!)) {
        _cart.set(item.store.id!, {
          ...item.store,
          checkout: 0,
          products: new Array<
            ProductType & {
              count: number;
              totalPrice: number;
              cartItemId: number;
            }
          >(),
        });
      }
      _cart.get(item.store.id!)!.products.push({
        ...item.product,
        count: item.count,
        totalPrice: item.count * item.product.price,
        cartItemId: item.id,
      });
      const storeCart = _cart.get(item.store.id!);
      if (storeCart && storeCart.checkout !== undefined) {
        storeCart.checkout += item.count * item.product.price;
      }
    });

    const __cart = Array.from(_cart.values());
    setCart(__cart);
    setLoading(null);
  }, [cartState.cart]);

  useEffect(() => {
    if (message?.type) {
      setOpenToast(true);
    }
  }, [message]);
  const deleteCart = async (store: storeCart) => {
    setLoading({ state: true, scope: `container-${store.id}` });

    let products = store.products as Array<
      ProductType & {
        count: number;
        totalPrice: number;
        cartItemId: number;
      }
    >;
    for (let i = 0; i < products.length; i++) {
      await deleteCartItem({ cartItemId: products[i].cartItemId }).then(
        (res) => {
          if (res.error) {
            setMessage({ text: res.error, type: 'error' });
            setOpenToast(true);
          }
        }
      );
    }

    await getAllCartItems()(cartDispatch);
  };
  const shareStoreCart = async (_cart: storeCart[]) => {
    let url = new URL(window.location.origin);
    url.pathname = '/shared-cart';
    for (let i = 0; i < _cart.length; i++) {
      url.searchParams.append('storeId', _cart[0].id!?.toString());
    }
    const isLoggedIn = authService.isLoggedIn();
    if (isLoggedIn) {
      const user = authService.getCurrentUser();
      url.searchParams.set('userId', encodeURIComponent(user.id));
    } else {
      const session = getSession();
      url.searchParams.set('sessionId', encodeURIComponent(session?.id!));
    }
    const result =  await share({
      text: "Check out my cart on Bazaarcheap!",
      url: url.toString(),
    });

    if (result.error) {
       setMessage({
          type: 'error',
          text: result.error as string,
        });
    }
  };

  const whatsAppCheckout = (_cart: storeCart) => {
    let url = new URL(window.location.origin);
    url.pathname = '/shared-cart';
    url.searchParams.append('storeId', _cart.id!?.toString());
    const isLoggedIn = authService.isLoggedIn();
    if (isLoggedIn) {
      const user = authService.getCurrentUser();
      url.searchParams.set('userId', encodeURIComponent(user.id));
    } else {
      const session = getSession();
      url.searchParams.set('sessionId', encodeURIComponent(session?.id!));
    }
    const message = `Hey!!, I'd like to place an order on your store on Bazaarcheap. Here's my cart ${url.toString()} `;
    let wa = new URL(`https://wa.me/${_cart.whatsappContact!}`);
    wa.searchParams.set('text', message);

    const win = window.open(wa, '_blank');
    if (win != null) {
      win.focus();
    }
  };

  return (
    <>
      <ErrorToast
        isOpen={openToast}
        message={message?.text!}
        onClose={() => {
          setOpenToast(false);
        }}
        type={message?.type!}
        position="bottom"
        
      />
      <SEOHeader
        title="My Cart | Bazaarcheap"
        description="My Cart on Bazaarcheap.com"
        url={window.location.href}
        ogImage={
          window.location.origin + '/assets/logo/logo_white_background_96.svg'
        }
      />
      <div className="w-full h-[10dvh] overflow-hidden flex flex-col items-center justify-evenly p-2 bg-white">
        <Nav />
      </div>
      <div className="w-full h-[90dvh] overflow-hidden flex flex-col items-center justify-evenly py-2 px-4 md:px-[10%]  bg-white">
        <div className="w-full h-[8%] md:h-[10%] flex items-center justify-start py-[1%]">
          <div className="h-full w-auto aspect-square flex items-center justify-center">
            <BackButton />
          </div>
          <div className="h-full w-auto ml-2 flex items-center overflow-hidden py-[1%] md:py-[5px]">
            <span className="h-full w-auto aspect-square flex items-center justify-center mx-2">
              <IoCartOutline size="100%" className="size-full items-primary" />
            </span>
            <p className="w-auto text-2xl font-semibold text-primary">Cart</p>
          </div>
        </div>
        <div className="w-full h-[90%] bg-background rounded-2xl p-2 ">
          <div className="size-full px-2 md:px-[12%] overflow-y-scroll">
            {cart.length ? (
              <>
                {cart.map((store, index) => {
                  cartBreakdown.push(
                    <div className="w-full flex h-auto justify-between items-center mb-[3px]">
                      <p className="font-semibold font-roboto text-lg text-primary ">
                        {store.name}
                      </p>
                      <p className="font-medium font-roboto text-lg text-primary">
                        UGX{' '}
                        {store.checkout?.toLocaleString('en-US', {
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    </div>
                  );

                  return (
                    <div
                      className="w-full h-auto flex flex-col items-center my-4"
                      key={store.id}
                    >
                      <div className="w-full flex items-center justify-between h-auto border-b-2  px-2 py-[5px]">
                        <p className="font-medium font-roboto text-medium md:text-xl text-primary">
                          {store.name}
                        </p>
                        <div
                          className="size-auto flex items-center rounded-xl overflow-hidden relative ion-activatable ripple-parent px-4 py-[2%] md:py-2  cursor-pointer"
                          onClick={() => {
                            deleteCart(store);
                          }}
                        >
                          <IonRippleEffect />
                          <span className="size-4 flex items-center justify-center">
                            <IoTrashBinOutline
                              size="100%"
                              className="size-full text-red-500"
                            />
                          </span>
                          <p className="ml-2 font-medium font-roboto text-base text-red-500 ">
                            Remove
                          </p>
                        </div>
                      </div>
                      <div className=" relative w-full h-auto grid grid-cols-2 md:grid-cols-4 grid-flow-row-dense gap-2 my-4">
                        {(
                          store.products as Array<
                            ProductType & {
                              count: number;
                              totalPrice: number;
                              cartItemId: number;
                            }
                          >
                        ).map((product, index) => {
                          return (
                            <div
                              className="size-auto flex items-center justify-center"
                              key={`product-${product.id}`}
                            >
                              <CartItemCard product={product} />
                            </div>
                          );
                        })}

                        {loading &&
                        loading.state &&
                        loading.scope === `container-${store.id}` ? (
                          <div className="absolute size-full z-20 flex items-center justify-center text-primary py-[4px] md:py-[3px] px-[2px] bg-[rgba(255,255,255,0.4)]">
                            <Spinner type="crescent" />
                          </div>
                        ) : (
                          ''
                        )}
                      </div>
                      <div className="w-full h-auto flex flex-col px-2 my-2 mt-4">
                        <div className="w-full flex h-auto justify-between items-center">
                          <p className="font-semibold font-roboto text-lg text-primary">
                            Total
                          </p>
                          <p className="font-medium font-roboto text-lg text-primary">
                            UGX{' '}
                            {store.checkout.toLocaleString('en-US', {
                              maximumFractionDigits: 2,
                            })}
                          </p>
                        </div>
                        <div className="w-full h-auto flex flex-col md:flex-row items-center justify-center mt-2 gap-2">
                          <IonButton
                            color="primary"
                            fill={store.whatsappContact ? 'outline' : 'solid'}
                            expand="block"
                            className=" w-full aspect-square flex items-center"
                            onClick={() => {
                              shareStoreCart([store]);
                            }}
                          >
                            <span className="mr-2 size-[25px]">
                              <IoMdShareAlt size="100%" />
                            </span>
                            Share Cart
                          </IonButton>

                          {store.whatsappContact && (
                            <IonButton
                              color="primary"
                              fill="solid"
                              expand="block"
                              className=" w-full aspect-square flex items-center"
                              onClick={() => {
                                whatsAppCheckout(store);
                              }}
                            >
                              <span className="mr-2 size-[25px]">
                                <MdShoppingCartCheckout size="100%" />
                              </span>
                              Place Order
                            </IonButton>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {cart.length > 1 ? (
                  <div className="w-full h-auto flex flex-col px-2 my-2 mt-4 border-t-2 py-4 ">
                    {cartBreakdown}
                    <div className="w-full flex h-auto justify-between items-center mt-4">
                      <p className="font-semibold font-roboto text-lg text-primary ">
                        Total
                      </p>
                      <p className="font-medium font-roboto text-lg text-primary">
                        UGX{' '}
                        {cartState.total?.toLocaleString('en-US', {
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    </div>
                    <div className="w-full h-auto flex items-center justify-center mt-2">
                      <IonButton
                        color="primary"
                        fill="solid"
                        expand="block"
                        className=" w-full aspect-square flex items-center"
                        onClick={() => {
                          shareStoreCart([]);
                        }}
                      >
                        <span className="mr-2 size-[25px]">
                          <IoMdShareAlt size="100%" />
                        </span>
                        Share Full Cart
                      </IonButton>
                    </div>
                  </div>
                ) : (
                  ''
                )}
              </>
            ) : (
              <div className="size-full flex flex-col items-center justify-center px-8">
                <p className="text-lg font-normal font-roboto text-primary text-center">
                  No items yet.
                  <span
                    className="text-blue-600 underline cursor-pointer mx-2"
                    onClick={() => {
                      history.push('/stores');
                    }}
                  >
                    Browse
                  </span>
                  to add items to your cart.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Cart;
